import { useState, useMemo } from 'react';
import { CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { cn } from '@/shared/lib/utils';
import { useAdjustmentRequests, useAdjustmentRequestMutations } from '../hooks/useAdjustmentRequests';
import { useTimeEntries } from '../hooks/useTimeEntry';
import {
  formatDateShort,
  formatTimeBRT,
  formatSLA,
  toDateString,
  type TimeAdjustmentRequest,
  type TimeEntry,
  type AdjustmentStatus,
} from '../types';

// ==========================================
// Config de status
// ==========================================
const statusConfig: Record<AdjustmentStatus, { label: string; icon: React.ElementType; color: string }> = {
  PENDING: { label: 'Pendente', icon: Clock, color: 'text-amber-600 dark:text-amber-400' },
  APPROVED: { label: 'Aprovado', icon: CheckCircle, color: 'text-green-600 dark:text-green-400' },
  REJECTED: { label: 'Rejeitado', icon: XCircle, color: 'text-red-600 dark:text-red-400' },
};

// ==========================================
// Campos de horário para comparação
// ==========================================
const timeFields: { label: string; requestedKey: keyof TimeAdjustmentRequest['requestedData']; entryKey: keyof TimeEntry }[] = [
  { label: 'Entrada',       requestedKey: 'clockIn',    entryKey: 'clockIn' },
  { label: 'Saída almoço',  requestedKey: 'breakStart', entryKey: 'breakStart' },
  { label: 'Volta almoço',  requestedKey: 'breakEnd',   entryKey: 'breakEnd' },
  { label: 'Saída',         requestedKey: 'clockOut',   entryKey: 'clockOut' },
];

// ==========================================
// Tabela de comparação anterior → solicitado
// ==========================================
function ComparisonTable({
  request,
  entry,
}: {
  request: TimeAdjustmentRequest;
  entry: TimeEntry | undefined;
}) {
  // Mostra somente campos que tiveram ajuste solicitado
  const fieldsToShow = timeFields.filter(
    (f) => !!request.requestedData[f.requestedKey]
  );

  if (fieldsToShow.length === 0) return null;

  const isApproved = request.status === 'APPROVED';

  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50">
            <th className="text-left py-1.5 px-3 font-medium text-muted-foreground text-xs">Campo</th>
            <th className="text-center py-1.5 px-3 font-medium text-muted-foreground text-xs">Anterior</th>
            <th className={cn(
              'text-center py-1.5 px-3 font-medium text-xs',
              isApproved
                ? 'text-green-600 dark:text-green-400'
                : 'text-amber-600 dark:text-amber-400'
            )}>
              {isApproved ? 'Aplicado' : 'Solicitado'}
            </th>
          </tr>
        </thead>
        <tbody>
          {fieldsToShow.map((f) => {
            const requestedVal = request.requestedData[f.requestedKey];

            // Prioridade para "anterior": originalData salvo no momento da criação.
            // Fallback para entry atual (compatibilidade com ajustes antigos sem originalData).
            const originalRaw = request.originalData
              ? (request.originalData[f.requestedKey] as string | null | undefined)
              : (entry?.[f.entryKey] as string | null | undefined);

            const originalFormatted = formatTimeBRT(originalRaw);
            const hasChange = requestedVal && originalFormatted !== '--:--' && requestedVal !== originalFormatted;

            return (
              <tr key={f.requestedKey} className="border-t">
                <td className="py-1.5 px-3 text-muted-foreground font-medium text-xs">{f.label}</td>

                {/* Coluna "Anterior" — sempre visível, usa snapshot original */}
                <td className="py-1.5 px-3 text-center font-mono text-xs">
                  {originalFormatted !== '--:--' ? (
                    <span className={cn(hasChange ? 'line-through text-red-500 dark:text-red-400' : 'text-foreground')}>
                      {originalFormatted}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>

                {/* Coluna "Solicitado" / "Aplicado" */}
                <td className="py-1.5 px-3 text-center font-mono text-xs">
                  {requestedVal ? (
                    <span className={cn(
                      'font-semibold',
                      isApproved
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-amber-600 dark:text-amber-400'
                    )}>
                      {requestedVal}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ==========================================
// Modal de revisão (gestor)
// ==========================================
interface ReviewModalProps {
  request: TimeAdjustmentRequest;
  entry: TimeEntry | undefined;
  onClose: () => void;
}

function ReviewModal({ request, entry, onClose }: ReviewModalProps) {
  const { review } = useAdjustmentRequestMutations();
  const [note, setNote] = useState('');

  const handleReview = async (status: 'APPROVED' | 'REJECTED') => {
    await review.mutateAsync({ id: request.id, status, reviewNote: note || undefined });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl border w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-1">Revisar Solicitação</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {request.employee?.user?.name ?? request.employeeId} — {formatDateShort(request.date)}
          </p>

          <div className="space-y-4 mb-4">
            {/* Comparação atual vs solicitado */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Comparação de horários</p>
              <ComparisonTable request={request} entry={entry} />
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Justificativa</p>
              <p className="text-sm bg-muted/40 rounded-lg px-3 py-2">{request.justification}</p>
            </div>
          </div>

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota de revisão (opcional)"
            rows={2}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary mb-4"
          />

          <div className="flex gap-3">
            <button
              onClick={() => handleReview('APPROVED')}
              disabled={review.isPending}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-green-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              Aprovar
            </button>
            <button
              onClick={() => handleReview('REJECTED')}
              disabled={review.isPending}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-red-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              <XCircle className="h-4 w-4" />
              Rejeitar
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium rounded-lg border hover:bg-accent transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Badge de SLA
// ==========================================
function SLABadge({ sla }: { sla: TimeAdjustmentRequest['sla'] }) {
  if (!sla) return null;

  const slaConfig = {
    ok: {
      bg: 'bg-green-100 dark:bg-green-950/40',
      text: 'text-green-700 dark:text-green-400',
      border: 'border-green-200 dark:border-green-900',
    },
    warning: {
      bg: 'bg-amber-100 dark:bg-amber-950/40',
      text: 'text-amber-700 dark:text-amber-400',
      border: 'border-amber-200 dark:border-amber-900',
    },
    overdue: {
      bg: 'bg-red-100 dark:bg-red-950/40',
      text: 'text-red-700 dark:text-red-400',
      border: 'border-red-200 dark:border-red-900',
    },
    completed: {
      bg: 'bg-muted',
      text: 'text-muted-foreground',
      border: 'border-border',
    },
  };

  const config = slaConfig[sla.status];

  // Para ajustes já concluídos (aprovados/rejeitados), mostra tempo decorrido
  if (sla.status === 'completed' || sla.status === 'overdue') {
    const absHours = Math.abs(sla.hoursElapsed);
    let elapsedText = '';

    if (absHours < 1) {
      const minutes = Math.round(absHours * 60);
      elapsedText = `${minutes}min`;
    } else if (absHours < 24) {
      const hours = Math.floor(absHours);
      const minutes = Math.round((absHours - hours) * 60);
      elapsedText = minutes > 0 ? `${hours}h${minutes}min` : `${hours}h`;
    } else {
      const days = Math.floor(absHours / 24);
      const hours = Math.floor(absHours % 24);
      elapsedText = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }

    return (
      <div className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border', config.bg, config.text, config.border)}>
        {sla.status === 'overdue' && <AlertTriangle className="h-3 w-3" />}
        <span>{sla.status === 'overdue' ? `Atrasado (${elapsedText})` : `Concluído em ${elapsedText}`}</span>
      </div>
    );
  }

  // Para ajustes pendentes, mostra tempo restante
  return (
    <div className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border', config.bg, config.text, config.border)}>
      {sla.isOverdue && <AlertTriangle className="h-3 w-3" />}
      <span>{formatSLA(sla.hoursRemaining)}</span>
    </div>
  );
}

// ==========================================
// Linha de solicitação
// ==========================================
function RequestRow({
  request,
  entry,
  isGestor,
}: {
  request: TimeAdjustmentRequest;
  entry: TimeEntry | undefined;
  isGestor: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const config = statusConfig[request.status];
  const StatusIcon = config.icon;

  return (
    <>
      {reviewing && (
        <ReviewModal
          request={request}
          entry={entry}
          onClose={() => setReviewing(false)}
        />
      )}
      <div className="border rounded-lg overflow-hidden">
        {/* Cabeçalho clicável */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors text-left"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <StatusIcon className={cn('h-4 w-4 shrink-0', config.color)} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                {isGestor && request.employee?.user?.name
                  ? `${request.employee.user.name} — `
                  : ''}
                {formatDateShort(request.date)}
              </p>
              <p className="text-xs text-muted-foreground line-clamp-1">{request.justification}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SLABadge sla={request.sla} />
            <span className={cn('text-xs font-medium', config.color)}>{config.label}</span>
            {expanded
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </button>

        {/* Detalhes expandidos */}
        {expanded && (
          <div className="px-4 pb-4 pt-3 border-t bg-muted/10 space-y-3">
            {/* Comparação */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Horários: registrado → solicitado</p>
              <ComparisonTable request={request} entry={entry} />
            </div>

            {/* Justificativa */}
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Justificativa</p>
              <p className="text-sm">{request.justification}</p>
            </div>

            {/* Nota de revisão (se houver) */}
            {request.reviewNote && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Nota do revisor</p>
                <p className="text-sm">{request.reviewNote}</p>
              </div>
            )}

            {/* Ação para gestor */}
            {isGestor && request.status === 'PENDING' && (
              <button
                onClick={() => setReviewing(true)}
                className="mt-1 flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Revisar solicitação
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ==========================================
// AdjustmentRequestList
// ==========================================
interface AdjustmentRequestListProps {
  isGestor?: boolean;
  employeeId?: string;
  statusFilter?: string;
}

export function AdjustmentRequestList({ isGestor = false, employeeId, statusFilter }: AdjustmentRequestListProps) {
  const { data: requests, isLoading } = useAdjustmentRequests({
    employeeId,
    status: statusFilter,
  });

  // Ordena por SLA: pendentes primeiro (por urgência), depois por data de criação
  const sortedRequests = useMemo(() => {
    if (!requests) return [];

    return [...requests].sort((a, b) => {
      // PENDING sempre vem primeiro
      if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
      if (b.status === 'PENDING' && a.status !== 'PENDING') return 1;

      // Entre PENDING, ordena por SLA (mais urgentes primeiro)
      if (a.status === 'PENDING' && b.status === 'PENDING') {
        const aSLA = a.sla?.hoursRemaining ?? Infinity;
        const bSLA = b.sla?.hoursRemaining ?? Infinity;
        // Menor hoursRemaining = mais urgente (ou atrasado se negativo)
        return aSLA - bSLA;
      }

      // Entre não-PENDING, ordena por data de criação (mais recente primeiro)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [requests]);

  // Deriva o intervalo de datas das solicitações para buscar as entradas originais
  const dateRange = (() => {
    if (!sortedRequests || sortedRequests.length === 0) return undefined;
    const dates = sortedRequests.map((r) => r.date.slice(0, 10)).sort();
    return { startDate: dates[0], endDate: dates[dates.length - 1] };
  })();

  const { data: entries } = useTimeEntries(
    dateRange
      ? { startDate: dateRange.startDate, endDate: dateRange.endDate, employeeId }
      : undefined
  );

  // Mapeia entries por data YYYY-MM-DD
  const entriesByDate = (entries ?? []).reduce<Record<string, TimeEntry>>((acc, e) => {
    acc[toDateString(e.date)] = e;
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {isGestor ? 'Solicitações de Ajuste' : 'Minhas Solicitações'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-14 bg-muted rounded-lg" />
            ))}
          </div>
        ) : sortedRequests.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Nenhuma solicitação encontrada.
          </p>
        ) : (
          <div className="space-y-2">
            {sortedRequests.map((req) => {
              const dateKey = toDateString(req.date);
              return (
                <RequestRow
                  key={req.id}
                  request={req}
                  entry={entriesByDate[dateKey]}
                  isGestor={isGestor}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
